import { useState, useEffect, useCallback } from "react";
import { sendError } from "@/lib/client/sentry";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

/**
 * Hook for fetching extra details for an item shown in a slide-in panel.
 *
 * Fetches additional item details when an ID is provided. Resets data
 * when ID is undefined.
 *
 * @param id - The item ID to fetch details for (optional)
 * @returns Object containing:
 * - extraDetails: The fetched item details
 * - loading: Boolean indicating if data is being fetched
 *
 * @example
 * ```tsx
 * function SlideinPanel({ itemId }) {
 *   const { extraDetails, loading } = useGetSlideinDetails(itemId);
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <Panel>
 *       <h2>{extraDetails?.name}</h2>
 *       <p>{extraDetails?.description}</p>
 *     </Panel>
 *   );
 * }
 * ```
 */
const useGetSlideinDetails = (id?: number) => {
  const [extraDetails, setExtraDetails] = useState<ExtraDetails>();
  const [loading, setLoading] = useState(true);

  const fetchItem = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/search/extra-item-info`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      }).then((res) => res.json());

      // v2 API wraps results in { success, data: [...] }
      const items: ExtraDetails[] = response?.data ?? response;
      if (Array.isArray(items) && items.length > 0) {
        setExtraDetails(items[0]);
      }
    } catch (error) {
      console.warn("Error fetching slidein details:", error);
      sendError(error as Error, { id });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchItem();
    } else {
      setExtraDetails(undefined);
      setLoading(false);
    }
  }, [id, fetchItem]);

  return {
    extraDetails,
    loading,
  };
};

export default useGetSlideinDetails;
